import React from 'react';
import { Link } from 'react-router-dom';
import { FaCar, FaCog, FaChartLine, FaTools, FaFileInvoice, FaHandshake } from 'react-icons/fa';
import { MdBusiness, MdOutlineEngineering } from 'react-icons/md';
import Header from '../components/Header';
import Footer from '../components/Footer';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#e02f2f"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const FeatureCard = styled.div`
  padding: 25px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#e02f2f"};
`;

const Section = styled.section`
  padding: 70px 0;
  background: ${props => props.background || "white"};
`;

const ListItem = styled.li`
  display: flex;
  align-items: start;
  margin-bottom: 12px;
`;

const BulletPoint = styled.div`
  color: ${props => props.color || "#e02f2f"};
  margin-right: 10px;
  font-weight: bold;
  font-size: 18px;
  line-height: 1.4;
`;

const MecaniqueTransformation = () => {
  const services = [
    {
      icon: <FaFileInvoice />,
      title: "Expertise Comptable et Fiscale",
      description: "Gestion rigoureuse de votre comptabilité et optimisation fiscale adaptée à votre entreprise.",
      color: "#e02f2f",
      items: [
        "Tenue et révision comptable en conformité avec les normes",
        "Établissement des comptes annuels et liasses fiscales",
        "Optimisation fiscale et gestion des déclarations",
        "Gestion de la paie et des obligations sociales",
        "Assistance lors des contrôles fiscaux"
      ]
    },
    {
      icon: <FaChartLine />,
      title: "Gestion et Pilotage Performance",
      description: "Outils et conseils pour suivre et améliorer vos performances opérationnelles et financières.",
      color: "#f0a500",
      items: [
        "Comptabilité analytique et suivi des coûts de production",
        "Tableaux de bord personnalisés et KPI pertinents",
        "Budgets prévisionnels et suivi budgétaire",
        "Analyse financière approfondie de votre activité",
        "Optimisation des coûts et gestion de trésorerie"
      ]
    },
    {
      icon: <MdBusiness />,
      title: "Conseil et Stratégie",
      description: "Accompagnement stratégique pour développer durablement votre entreprise industrielle.",
      color: "#e02f2f",
      items: [
        "Accompagnement à la création et à la reprise d'entreprise",
        "Conseil en investissement et analyse de rentabilité",
        "Optimisation de votre structure financière",
        "Évaluation d'entreprise pour cession ou acquisition",
        "Stratégie de croissance et transformation digitale"
      ]
    }
  ];

  const features = [
    {
      icon: <MdOutlineEngineering />,
      title: "Expertise Sectorielle",
      description: "Nous comprenons les spécificités et les enjeux propres au secteur de la mécanique et des transformations.",
      color: "#e02f2f"
    },
    {
      icon: <FaTools />,
      title: "Solutions Sur-mesure",
      description: "Nous adaptons nos services à vos besoins spécifiques pour vous offrir un accompagnement personnalisé.",
      color: "#f0a500"
    },
    {
      icon: <FaCog />,
      title: "Approche Proactive",
      description: "Nous anticipons les évolutions de votre secteur pour vous aider à saisir les opportunités et éviter les risques.",
      color: "#e02f2f"
    }
  ];

  return (
    <>
      <Header />

      {/* Banner Section */}
      <section className="service-banner d-flex align-items-center">
        <div className="container-fluid px-4 px-md-5 text-white">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="fw-bold display-4 mb-3">Mécanique & Transformation</h1>
              <p className="fs-5 mb-4">Expertise comptable et conseil stratégique pour les entreprises industrielles</p>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-white text-decoration-none">
                      Accueil
                    </Link>{" "}
                    /{" "}
                    <Link to="/services" className="text-white text-decoration-none">
                      Services
                    </Link>
                  </li>
                  <li className="breadcrumb-item active text-white" aria-current="page">
                    Mécanique & Transformation
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row">
            <div className="col-12">
              <h2 className="fw-bold custom-title text-center mb-5">
                Optimisez la performance et la croissance de votre entreprise industrielle 
                avec nos solutions d'expertise comptable et de conseil
              </h2>
              <p className="text-muted text-center fs-5 mb-5">
                Le secteur de la mécanique et des transformations est exigeant : innovation constante, 
                investissements importants, concurrence accrue, réglementations spécifiques... 
                Notre cabinet comprend les défis uniques auxquels votre entreprise est confrontée et 
                vous accompagne dans toutes les étapes de votre développement.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Services Section */}
      <Section background="#f8f9fa">
        <div className="container-fluid px-4 px-md-5">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="fw-bold mb-1">Nos services spécialisés</h2>
              <div className="d-inline-block mb-4">
                <div className="custom-underline mx-auto"></div>
              </div>
              <p className="text-muted fs-5 mb-5">Des solutions adaptées aux besoins spécifiques de votre entreprise industrielle</p>
            </div>
          </div>

          <div className="row">
            {services.map((service, index) => (
              <div className="col-md-6 col-lg-4 mb-4" key={index}>
                <ServiceCard delay={`${index * 0.1}s`}>
                  <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <ul className="ps-3 mt-4">
                    {service.items.map((item, idx) => (
                      <ListItem key={idx}>
                        <BulletPoint color={service.color}>•</BulletPoint>
                        <span>{item}</span>
                      </ListItem>
                    ))}
                  </ul>
                </ServiceCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8">
              <div className="call-to-action p-5 rounded-3 text-center">
                <h3 className="fw-bold text-white mb-3">Prêt à donner un nouvel élan à votre entreprise ?</h3>
                <p className="text-white fs-5 mb-4">
                  Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée. 
                  Nous serons ravis d'échanger sur vos projets et de vous proposer des solutions sur mesure.
                </p>
                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un rendez-vous</a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Features Section */}
      <Section background="#f8f9fa">
        <div className="container-fluid px-4 px-md-5">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h3 className="fw-bold">Pourquoi nous choisir ?</h3>
              <div className="d-inline-block mb-4">
                <div className="custom-underline mx-auto"></div>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            {features.map((feature, index) => (
              <div className="col-md-4 col-sm-6 mb-4" key={index}>
                <FeatureCard>
                  <FeatureIcon color={feature.color}>{feature.icon}</FeatureIcon>
                  <h4 className="fw-bold fs-5 mb-3">{feature.title}</h4>
                  <p className="text-muted mb-0">{feature.description}</p>
                </FeatureCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Corporate Finance Section */}
      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h3 className="fw-bold custom-title mb-4">Solutions de Corporate Finance</h3>
              <p className="text-muted mb-4">
                Notre équipe d'experts vous accompagne dans vos opérations financières 
                stratégiques pour soutenir votre croissance et vos transformations d'entreprise.
              </p>
              
              <div className="d-flex mb-3">
                <div className="me-3">
                  <FaHandshake className="fs-3 text-danger" />
                </div>
                <div>
                  <h5 className="fw-bold">Levée de fonds</h5>
                  <p className="text-muted">
                    Accompagnement pour trouver des financements adaptés 
                    à votre développement industriel.
                  </p>
                </div>
              </div>
              
              <div className="d-flex mb-3">
                <div className="me-3">
                  <FaHandshake className="fs-3 text-warning" />
                </div>
                <div>
                  <h5 className="fw-bold">Fusions et Acquisitions</h5>
                  <p className="text-muted">
                    Conseil et assistance lors de cessions, acquisitions, 
                    ou rapprochements d'entreprises.
                  </p>
                </div>
              </div>
              
              <div className="d-flex">
                <div className="me-3">
                  <FaHandshake className="fs-3 text-danger" />
                </div>
                <div>
                  <h5 className="fw-bold">Restructuration financière</h5>
                  <p className="text-muted">
                    Accompagnement pour renégocier vos dettes ou trouver 
                    de nouvelles sources de financement.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="bg-light p-5 rounded-3">
                <div className="text-center mb-4">
                  <FaCar className="display-1 text-danger" />
                </div>
                <h4 className="fw-bold text-center mb-3">Une expertise spécifique pour votre industrie</h4>
                <p className="text-muted text-center">
                  Nous apportons des solutions adaptées à votre secteur d'activité, 
                  en tenant compte des spécificités de votre chaîne de production, 
                  de vos processus et de vos enjeux commerciaux.
                </p>
                <div className="text-center mt-4">
                  <Link to="/businesscreationform" className="btn btn-danger px-4 py-2 fw-bold">
                    Contactez nos experts
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(224, 47, 47, 0.85), rgba(240, 165, 0, 0.85)), 
                      url("/assets/images/mecanique-transformation.jpg") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #e02f2f, #f0a500);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #e02f2f, #f0a500);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #e02f2f, #f0a500);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </>
  );
};

export default MecaniqueTransformation;